import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { MapPin, Clock, Briefcase, ArrowRight, Send, CheckCircle, Building } from 'lucide-react';
import { careers, companyInfo } from '../data/mock';

const CareersPage = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    resume: '',
    coverLetter: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleApply = (job) => {
    setSelectedJob(job);
    setIsSubmitted(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setSelectedJob(null);
      setIsSubmitted(false);
      setApplicationData({ name: '', email: '', phone: '', resume: '', coverLetter: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#B8336A]/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#2D6A6A]/20 blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Careers</span>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Join Our Team at
              <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> AMTB Solution</span>
            </h1>
            <p className="text-gray-300 text-lg">
              We're looking for passionate individuals who want to make a difference in the tech industry. Explore our open positions and become part of our growing team.
            </p>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-sm font-medium text-[#B8336A] mb-4">
              Open Positions
            </span>
            <h2 className="text-3xl font-bold text-gray-900">
              Current Job Openings
            </h2>
          </div>

          <div className="space-y-4">
            {careers.map((job, index) => (
              <div
                key={job.id}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#B8336A]/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-[#B8336A]">
                        {String(index + 1).padStart(2, '0')}.
                      </span>
                      <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} className="text-[#2D6A6A]" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase size={14} className="text-[#2D6A6A]" />
                        {job.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} className="text-[#2D6A6A]" />
                        {job.experience}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-3">{job.description}</p>
                  </div>
                  <button
                    onClick={() => handleApply(job)}
                    className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-medium hover:shadow-lg hover:shadow-[#B8336A]/30 transition-all"
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Join
                <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> AMTB Solution?</span>
              </h2>
              <div className="space-y-4">
                {[
                  'Work on cutting-edge technologies',
                  'Flexible work environment',
                  'Competitive compensation',
                  'Learning and development opportunities',
                  'Collaborative team culture',
                  'Health and wellness benefits'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center">
                      <CheckCircle size={14} className="text-white" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600"
                alt="Team"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
                <p className="text-gray-600">We'll review your application and get back to you soon.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Apply for {selectedJob.title}</h3>
                    <p className="text-gray-600">{selectedJob.location}</p>
                  </div>
                  <button
                    onClick={() => setSelectedJob(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#B8336A] focus:ring-2 focus:ring-[#B8336A]/20 outline-none"
                    value={applicationData.name}
                    onChange={(e) => setApplicationData({...applicationData, name: e.target.value})}
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#B8336A] focus:ring-2 focus:ring-[#B8336A]/20 outline-none"
                    value={applicationData.email}
                    onChange={(e) => setApplicationData({...applicationData, email: e.target.value})}
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#B8336A] focus:ring-2 focus:ring-[#B8336A]/20 outline-none"
                    value={applicationData.phone}
                    onChange={(e) => setApplicationData({...applicationData, phone: e.target.value})}
                  />
                  <input
                    type="url"
                    placeholder="Resume/LinkedIn URL"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#B8336A] focus:ring-2 focus:ring-[#B8336A]/20 outline-none"
                    value={applicationData.resume}
                    onChange={(e) => setApplicationData({...applicationData, resume: e.target.value})}
                  />
                  <textarea
                    placeholder="Cover Letter (optional)"
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#B8336A] focus:ring-2 focus:ring-[#B8336A]/20 outline-none resize-none"
                    value={applicationData.coverLetter}
                    onChange={(e) => setApplicationData({...applicationData, coverLetter: e.target.value})}
                  />
                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                  >
                    <Send size={18} />
                    Submit Application
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CareersPage;
